import { useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { RECOMMENDATION_SOURCE_LABELS, RECOMMENDATION_TYPE_LABELS, RECOMMENDATION_TYPES, type HouseholdDetail, type RecommendationType } from '@hv/shared';
import { useAddRecommendation } from '../api/hooks';
import { citeAnchor } from '../assistant/citations';
import { useAuth } from '../components/AuthContext';
import { RecTypeTag } from '../components/badges';
import { useNotify } from '../components/Notify';
import { BRAND } from '../theme';
import { Section } from './Section';

const FIELDS: { key: 'connection' | 'valueAdd' | 'familyDetails' | 'academicStanding' | 'disciplinaryStanding' | 'willEnroll' | 'additionalInfo'; label: string }[] = [
  { key: 'connection', label: 'Connection' },
  { key: 'valueAdd', label: 'Value to Gulliver' },
  { key: 'familyDetails', label: 'Family' },
  { key: 'academicStanding', label: 'Academic standing' },
  { key: 'disciplinaryStanding', label: 'Disciplinary standing' },
  { key: 'willEnroll', label: 'Will enroll?' },
  { key: 'additionalInfo', label: 'Additional information' },
];

export function RecommendationsSection({ h }: { h: HouseholdDetail }) {
  const { can, me } = useAuth();
  const { notify, notifyError } = useNotify();
  const add = useAddRecommendation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<RecommendationType>('internal');
  const [recommender, setRecommender] = useState(me.displayName);
  const [connection, setConnection] = useState('');
  const [valueAdd, setValueAdd] = useState('');
  const [info, setInfo] = useState('');

  const submit = () =>
    add.mutate(
      {
        id: h.id,
        input: {
          type,
          recommenderName: recommender.trim(),
          connection: connection.trim() || null,
          valueAdd: valueAdd.trim() || null,
          familyDetails: null,
          academicStanding: null,
          disciplinaryStanding: null,
          willEnroll: null,
          additionalInfo: info.trim() || null,
        },
      },
      {
        onSuccess: () => {
          setOpen(false);
          setConnection('');
          setValueAdd('');
          setInfo('');
          notify('success', 'Recommendation added; re-scoring has been queued');
        },
        onError: (err) => notifyError(err, 'Adding recommendation failed'),
      },
    );

  return (
    <Section
      title="Recommendations"
      action={
        can('recommendations.write') && !open ? (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            Add
          </Button>
        ) : undefined
      }
    >
      {open && (
        <Box sx={{ p: 1.5, mb: 1.5, border: `1px solid ${BRAND.line}`, borderRadius: 2 }}>
          <Stack gap={1.5}>
            <Stack direction="row" gap={1}>
              <TextField select size="small" label="Type" value={type} onChange={(e) => setType(e.target.value as RecommendationType)} sx={{ width: 200 }}>
                {RECOMMENDATION_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {RECOMMENDATION_TYPE_LABELS[t]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField size="small" label="Recommender" required value={recommender} onChange={(e) => setRecommender(e.target.value)} sx={{ flex: 1 }} />
            </Stack>
            <TextField size="small" label="How are you connected to this family?" multiline minRows={2} value={connection} onChange={(e) => setConnection(e.target.value)} />
            <TextField size="small" label="How can this family add value to Gulliver?" multiline minRows={2} value={valueAdd} onChange={(e) => setValueAdd(e.target.value)} />
            <TextField size="small" label="Additional information" multiline minRows={2} value={info} onChange={(e) => setInfo(e.target.value)} />
            <Stack direction="row" gap={1}>
              <Button variant="contained" disabled={!recommender.trim() || add.isPending} onClick={submit}>
                Save recommendation
              </Button>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
            </Stack>
          </Stack>
        </Box>
      )}
      {h.recommendations.length === 0 && !open && (
        <Typography variant="body2" color="text.secondary">
          No recommendations recorded.
        </Typography>
      )}
      <Stack gap={1.5}>
        {h.recommendations.map((r) => (
          <Box key={r.id} {...citeAnchor(`recommendation:${r.id}`)} sx={{ p: 1.5, border: `1px solid ${BRAND.line}`, borderRadius: 2 }}>
            <Stack direction="row" gap={1} alignItems="center" mb={0.5} flexWrap="wrap">
              <RecTypeTag type={r.type} />
              <Typography variant="body2" fontWeight={700}>
                {r.recommenderName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {r.submittedAt.slice(0, 10)} · {RECOMMENDATION_SOURCE_LABELS[r.source]}
              </Typography>
            </Stack>
            {FIELDS.filter((f) => r[f.key]).map((f) => (
              <Typography key={f.key} variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                <Box component="span" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                  {f.label}:{' '}
                </Box>
                {r[f.key]}
              </Typography>
            ))}
          </Box>
        ))}
      </Stack>
    </Section>
  );
}
